import { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Calendar } from '@phosphor-icons/react';
import AppDateRangePickerModal from '../../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import { PaymentsTable } from './PaymentsTable';
import { InvoicePaymentsTable } from './InvoicePaymentsTable';
import { PaymentVolumeTable } from './PaymentVolumeTable';
import { usePaymentsData } from './usePaymentsData';
import { PaymentVolumeRow } from './types';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { getSupplierReport } from '../../../../../redux/services/reportsService';
import { DillSelect } from '../../../../general/Dill';

type TabType = 'payments' | 'invoicePayments' | 'volumeByMonth';

const PaymentsDataReporting = () => {
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { supplierAnnualReport, loadingList } = useAppSelector((state) => state.reports);

  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate?: Date;
    endDate?: Date;
  }>({
    startDate: moment(new Date()).subtract(3, 'months').toDate(),
    endDate: new Date(),
  });
  const [selectedYear, setSelectedYear] = useState(`${new Date().getFullYear()}`);
  const [activeTab, setActiveTab] = useState<TabType>('invoicePayments');

  const { payments, invoicePayments, isLoading } = usePaymentsData(dateRange);

  const isVolumeLoading = loadingList.includes('getSupplierReport');

  const yearsList = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i <= 5; i++) {
      years.push(`${currentYear - i}`);
    }
    return years;
  }, []);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierReport({ supplierId: currentSupplier.id, year: selectedYear }));
    }
  }, [currentSupplier?.id, selectedYear]);

  const volumeRows: PaymentVolumeRow[] = useMemo(
    () =>
      (supplierAnnualReport?.yearMonthlySummary ?? []).map((monthSummary) => ({
        id: monthSummary.id,
        monthLabel: `${monthSummary.month} ${monthSummary.year}`,
        month: monthSummary.month,
        year: monthSummary.year,
        totalVolume:
          (monthSummary.paymentTypes.CARD?.totalAmount || 0) +
          (monthSummary.paymentTypes.BANK?.totalAmount || 0),
        cardVolume: monthSummary.paymentTypes.CARD?.totalAmount || 0,
        bankVolume: monthSummary.paymentTypes.BANK?.totalAmount || 0,
      })),
    [supplierAnnualReport]
  );

  const formatDateRangeDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) return 'Select date range';
    return `${moment(dateRange.startDate).format('MMM DD, YYYY')} - ${moment(
      dateRange.endDate
    ).format('MMM DD, YYYY')}`;
  };

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: 'invoicePayments', label: 'ERP Reconciliation', count: invoicePayments.length },
    { key: 'payments', label: 'Bank Reconciliation', count: payments.length },
    { key: 'volumeByMonth', label: 'Payment Volume', count: volumeRows.length },
  ];

  return (
    <div className="flex flex-col w-full h-full pt-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-0">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-gray-500">
              For Dill Payments, access reports for ERP reconciliation, bank reconciliation, and
              monthly payment volume by method.
            </p>
          </div>
          {activeTab === 'volumeByMonth' ? (
            <div className="shrink-0 min-w-[120px]">
              <DillSelect
                value={selectedYear}
                options={yearsList.map((year) => ({ label: year, value: year }))}
                onChange={(value) => {
                  if (yearsList.includes(value)) {
                    setSelectedYear(value);
                  }
                }}
              />
            </div>
          ) : (
            <button
              onClick={() => setIsDateRangePickerOpen(true)}
              className="shrink-0 inline-flex items-center gap-2 h-[34px] px-3.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
              <Calendar size={16} weight="bold" />
              <span>{formatDateRangeDisplay()}</span>
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 mt-4 mb-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
              <span
                className={`ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
                  activeTab === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Table Content */}
        {activeTab === 'payments' && <PaymentsTable data={payments} isLoading={isLoading} />}
        {activeTab === 'invoicePayments' && (
          <InvoicePaymentsTable data={invoicePayments} isLoading={isLoading} />
        )}
        {activeTab === 'volumeByMonth' && (
          <PaymentVolumeTable data={volumeRows} isLoading={isVolumeLoading} year={selectedYear} />
        )}
      </div>

      {/* Date Range Picker Modal */}
      <AppDateRangePickerModal
        open={isDateRangePickerOpen}
        handleClose={() => setIsDateRangePickerOpen(false)}
        onSelectDateRange={(range) => {
          setDateRange(range);
          setIsDateRangePickerOpen(false);
        }}
        selectedDateRange={dateRange}
      />
    </div>
  );
};

export default PaymentsDataReporting;
